import React, { useEffect, useState } from 'react';
import { Button, Modal, Space, Typography } from 'antd';
import { ShareAltOutlined, PlusSquareOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

const DISMISSED_KEY = 'tesonInstallPromptDismissed';

export const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPadOS 13+ reports itself as a Mac; the touch point count gives it away.
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // iOS-only legacy flag; it is the only reliable signal on older Safari.
  window.navigator.standalone === true;

/**
 * iOS never fires `beforeinstallprompt`, so there is no way to offer an install
 * button the way Chrome does — the user has to go through the Share sheet. This
 * shows them how, once, and only where it is relevant: iOS Safari, not already
 * installed.
 */
const InstallPrompt = () => {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(DISMISSED_KEY) === '1';
    } catch (e) {
      // Private mode / blocked storage — just treat it as not dismissed.
    }

    if (dismissed || isStandalone()) return undefined;

    // Chrome/Edge/Android: capture the real install prompt so the same button
    // can trigger a proper install instead of instructions.
    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setOpen(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS: no event to wait for. Give the app a moment to settle so this does
    // not fight the login screen for attention.
    let timer;
    if (isIos()) {
      timer = setTimeout(() => setOpen(true), 4000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      window.localStorage.setItem(DISMISSED_KEY, '1');
    } catch (e) {
      // Nothing to do; worst case the hint shows again next visit.
    }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss();
  };

  return (
    <Modal
      open={open}
      onCancel={dismiss}
      centered
      title="Install Teson CRM"
      footer={
        deferredPrompt
          ? [
              <Button key="later" onClick={dismiss}>
                Not now
              </Button>,
              <Button key="install" type="primary" onClick={handleInstall}>
                Install
              </Button>,
            ]
          : [
              <Button key="ok" type="primary" onClick={dismiss}>
                Got it
              </Button>,
            ]
      }
    >
      {deferredPrompt ? (
        <Paragraph>
          Add Teson CRM to your device for a full-screen app that opens straight
          from your home screen.
        </Paragraph>
      ) : (
        <>
          <Paragraph>
            Add Teson CRM to your home screen to use it like an app — full
            screen, no address bar.
          </Paragraph>
          <Space direction="vertical" size={12}>
            <Space>
              <ShareAltOutlined style={{ fontSize: 18, color: '#FF8C00' }} />
              <Text>
                Tap the <Text strong>Share</Text> button in Safari&apos;s toolbar
              </Text>
            </Space>
            <Space>
              <PlusSquareOutlined style={{ fontSize: 18, color: '#FF8C00' }} />
              <Text>
                Choose <Text strong>Add to Home Screen</Text>
              </Text>
            </Space>
          </Space>
        </>
      )}
    </Modal>
  );
};

export default InstallPrompt;
